import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AboutPage } from './pages/AboutPage';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { SuspendedPage } from './pages/SuspendedPage';
import { TermsPage } from './pages/TermsPage';
import { useInstanceStore } from './store/useInstanceStore';
import { useDesktopNotifications } from './hooks/useDesktopNotifications';
import { fetchPublicInstance } from './lib/publicInstanceApi';
import { RegisterPage } from './pages/RegisterPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { DashboardRoute } from './components/DashboardRoute';
import { PeoplePage } from './pages/PeoplePage';
import { ActorsPage } from './pages/ActorsPage';
import { ActorDetailPage } from './pages/ActorDetailPage';
import { PersonDetailPage } from './pages/PersonDetailPage';
import { CapacityPlanningPage } from './pages/CapacityPlanningPage';
import { ProjectsListPage } from './pages/ProjectsListPage';
import { TasksPage } from './pages/TasksPage';
import { PersonalTasksPage } from './pages/PersonalTasksPage';
import { RemindersPage } from './pages/RemindersPage';
import { LeavePage } from './pages/LeavePage';
import { MeetingsPage } from './pages/MeetingsPage';
import { MeetingDetailPage } from './pages/MeetingDetailPage';
import { TaskDetailPage } from './pages/TaskDetailPage';
import { TaskReportsPage } from './pages/TaskReportsPage';
import { CalendarPage } from './pages/CalendarPage';
import { LeadsPage } from './pages/LeadsPage';
import { LeadsBoardPage } from './pages/LeadsBoardPage';
import { LeadDetailPage } from './pages/LeadDetailPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { ProjectFormPage } from './pages/ProjectFormPage';
import { ClientsListPage } from './pages/ClientsListPage';
import { ClientDetailPage } from './pages/ClientDetailPage';
import { SuppliersListPage } from './pages/SuppliersListPage';
import { SupplierDetailPage } from './pages/SupplierDetailPage';
import { SubscriptionsListPage } from './pages/SubscriptionsListPage';
import { SubscriptionDetailPage } from './pages/SubscriptionDetailPage';
import { FinancialsPage } from './pages/FinancialsPage';
import { MonthlyReportPage } from './pages/MonthlyReportPage';
import { CashflowPage } from './pages/CashflowPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { SpvPage } from './pages/SpvPage';
import { IssuedInvoiceEditorPage } from './pages/IssuedInvoiceEditorPage';
import { BankStatementsPage } from './pages/BankStatementsPage';
import { BankStatementReconcilePage } from './pages/BankStatementReconcilePage';
import { DailySummaryPage } from './pages/DailySummaryPage';
import { WeeklySummaryPage } from './pages/WeeklySummaryPage';
import { ChatPage } from './pages/ChatPage';
import { VoiceAssistantPage } from './pages/VoiceAssistantPage';
import { TeamChatPage } from './pages/TeamChatPage';
import { VoiceChannelPage } from './pages/VoiceChannelPage';
import { VoiceCallPage } from './pages/VoiceCallPage';
import { VoiceCallsPage } from './pages/VoiceCallsPage';
import { MeetPage } from './pages/MeetPage';
import { MeetHistoryPage } from './pages/MeetHistoryPage';
import { MeetRoomPage } from './pages/MeetRoomPage';
import { PublicClientCalendarPage } from './pages/PublicClientCalendarPage';
import { KnowledgeBasePage } from './pages/KnowledgeBasePage';
import { SettingsPage } from './pages/SettingsPage';
import { BugReportsPage } from './pages/BugReportsPage';
import { ProfilePage } from './pages/ProfilePage';
import { OAuthConsentPage } from './pages/OAuthConsentPage';
import { McpPage } from './pages/McpPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { CompanyDetailPage } from './pages/CompanyDetailPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/layout/Layout';
import { PwaPrompts } from './components/pwa/PwaPrompts';
import { useAuthStore } from './store/useAuthStore';

/** The public legal page (and its /privacy alias). */
function isLegalPath(pathname: string): boolean {
  return pathname === '/terms' || pathname === '/privacy';
}

export function App() {
  const { pathname } = useLocation();
  const initialize = useAuthStore((s) => s.initialize);
  const suspended = useInstanceStore((s) => s.suspended);
  const setSuspended = useInstanceStore((s) => s.setSuspended);

  // The desktop app's system notifications (no-op in a browser). Here rather
  // than in Layout so they keep coming during a Meet call, which has no Layout.
  useDesktopNotifications();

  // Validate/restore the persisted session once on app load.
  useEffect(() => {
    void initialize();
  }, [initialize]);

  // Ask once per load whether the Firmio Console suspended this instance, so
  // even the login page shows the notice instead of a form that cannot work.
  useEffect(() => {
    fetchPublicInstance()
      .then((info) => {
        if (info.licenseStatus === 'suspended') setSuspended(info.licenseMessage);
      })
      .catch(() => undefined);
  }, [setSuspended]);

  // The legal page stays reachable on a suspended instance: Google Play and
  // anyone asking for account deletion link to it.
  if (suspended && !isLegalPath(pathname)) return <SuspendedPage />;

  return (
    <>
      <Routes>
        {/* Public auth routes (no sidebar) */}
        <Route path="/login" element={<LoginPage />} />
        {/* Terms, privacy policy, account deletion: public, no login. */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<Navigate to="/terms#confidentialitate" replace />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
        <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
        {/* Firmio Meet room: public, full screen, no sidebar — guests join by
            link with no account, and a colleague in a video call has no use for
            the app shell either. Being outside the Layout also unmounts the
            voice provider, which hangs up any voice channel. */}
        <Route path="/meet/:code" element={<MeetRoomPage />} />
        {/* A client's content calendar: public link + PIN, no account. */}
        <Route path="/c/:token" element={<PublicClientCalendarPage />} />
        {/* Firmio MCP: an MCP client (Claude Desktop, claude.ai) asks for access.
            Signed-in only (anonymous visitors log in first and come back with
            the request intact), full screen like the auth pages, no sidebar. */}
        <Route
          path="/oauth/consent"
          element={
            <ProtectedRoute>
              <OAuthConsentPage />
            </ProtectedRoute>
          }
        />

        {/* Authenticated app shell: ProtectedRoute -> Layout (sidebar + outlet) */}
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<DashboardRoute />} />
          <Route path="/dashboard" element={<DashboardRoute />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />

          <Route path="/tasks" element={<TasksPage />} />
          {/* "Sarcini personale" — the caller's private task list, backed by a
              hidden per-user personal project. */}
          <Route path="/personal-tasks" element={<PersonalTasksPage />} />
          {/* "Remindere" — strictly-personal reminders (auth-only, ungated). */}
          <Route path="/reminders" element={<RemindersPage />} />
          {/* "Concedii" — filing your own leave is auth-only like Remindere;
              the team list and the report are gated inside the page on
              `leave.canViewAll`. `?cerere=<id>` is the deep link the
              notification emails and the push payload point at. */}
          <Route path="/concedii" element={<LeavePage />} />
          {/* CLE-184: dedicated Linear-style task detail page.
              CLE-274: canonical project-independent route so any task link in
              the app (dashboard, daily summary, client/project detail) resolves
              by id-or-identifier without needing a projectId. The legacy
              project-scoped route is kept for back-compat / nicer breadcrumbs. */}
          <Route path="/tasks/:taskIdentifier" element={<TaskDetailPage />} />
          <Route path="/projects/:projectId/tasks/:taskIdentifier" element={<TaskDetailPage />} />
          {/* Task reports (estimated vs. actual, overruns, throughput, …). */}
          <Route path="/reports/tasks" element={<TaskReportsPage />} />
          {/* Operational calendar: monthly task grid + project/team Gantt. */}
          <Route path="/calendar" element={<CalendarPage />} />

          {/* "Întâlniri" — meetings with external people + contact directory
              (RBAC module `meetings`). */}
          <Route path="/meetings" element={<MeetingsPage />} />
          <Route path="/meetings/:id" element={<MeetingDetailPage />} />

          {/* CLE-191: Leads board/list + pipeline-scoped board (spec 3.2). */}
          <Route path="/leads" element={<LeadsPage />} />
          <Route path="/leads/pipeline/:pipelineId" element={<LeadsBoardPage />} />
          {/* CLE-192: dedicated Lead detail page (properties, actions, timeline, convert). */}
          <Route path="/leads/:id" element={<LeadDetailPage />} />

          <Route path="/people" element={<PeoplePage />} />
          <Route path="/people/capacity" element={<CapacityPlanningPage />} />
          <Route path="/people/:id" element={<PersonDetailPage />} />
          {/* Actori (opt-in module): the route guard redirects when it is off. */}
          <Route path="/actors" element={<ActorsPage />} />
          <Route path="/actors/:id" element={<ActorDetailPage />} />

          <Route path="/projects" element={<ProjectsListPage />} />
          <Route path="/projects/new" element={<ProjectFormPage />} />
          <Route path="/projects/:id" element={<ProjectDetailPage />} />
          <Route path="/projects/:id/edit" element={<ProjectFormPage />} />

          <Route path="/clients" element={<ClientsListPage />} />
          <Route path="/clients/:id" element={<ClientDetailPage />} />

          <Route path="/suppliers" element={<SuppliersListPage />} />
          <Route path="/suppliers/:id" element={<SupplierDetailPage />} />

          <Route path="/subscriptions" element={<SubscriptionsListPage />} />
          <Route path="/subscriptions/:id" element={<SubscriptionDetailPage />} />

          <Route path="/financials" element={<FinancialsPage />} />
          <Route path="/financials/cashflow" element={<CashflowPage />} />
          {/* Reconciliation happens on each statement (2026-09-29); the old page is gone. */}
          <Route
            path="/financials/reconciliation"
            element={<Navigate to="/financials/bank-statements" replace />}
          />
          <Route path="/financials/bank-statements" element={<BankStatementsPage />} />
          <Route
            path="/financials/bank-statements/:id/reconcile"
            element={<BankStatementReconcilePage />}
          />
          <Route path="/financials/reports" element={<MonthlyReportPage />} />

          <Route path="/invoices" element={<InvoicesPage />} />
          {/* SmartBill is hidden from the UI (2026-09-29); old links land on Facturi. */}
          <Route path="/smartbill" element={<Navigate to="/invoices" replace />} />
          <Route path="/financials/spv" element={<SpvPage />} />
          {/* "Facturi emise" merged into Facturi (2026-09-29); the editor keeps its routes. */}
          <Route path="/financials/issued" element={<Navigate to="/invoices" replace />} />
          <Route path="/financials/issued/new" element={<IssuedInvoiceEditorPage />} />
          <Route path="/financials/issued/:id" element={<IssuedInvoiceEditorPage />} />

          <Route path="/onboarding" element={<Navigate to="/settings?tab=onboarding" replace />} />

          {/* CLE-434: current-user profile (name, email, language). */}
          <Route path="/profile" element={<ProfilePage />} />
          {/* Firmio MCP: how to use Firmio AI from Claude Desktop / claude.ai / IDEs. */}
          <Route path="/mcp" element={<McpPage />} />

          <Route path="/daily-summary" element={<DailySummaryPage />} />
          <Route path="/weekly-summary" element={<WeeklySummaryPage />} />
          {/* Email history moved into Settings; keep the old link working. */}
          <Route
            path="/admin/emails"
            element={<Navigate to="/settings?tab=email-history" replace />}
          />
          {/* The open thread lives in the URL so the conversation list can be
              rendered by the app sidebar, outside the page. */}
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/chat/:conversationId" element={<ChatPage />} />
          {/* Firmio Voice — the spoken Firmio AI (GPT-Live); same permission as /chat. */}
          <Route path="/voice" element={<VoiceAssistantPage />} />
          {/* Team chat (internal Discord-like messaging) — /chat is the AI
              assistant, so the team feature lives on /team-chat. */}
          <Route path="/team-chat" element={<TeamChatPage />} />
          {/* Static segment, so it outranks /:conversationId regardless of order. */}
          <Route path="/team-chat/voice/:channelId" element={<VoiceChannelPage />} />
          {/* Call history + transcripts. Static segment again, so `calls` is
              never taken for a conversation id. */}
          <Route path="/team-chat/calls" element={<VoiceCallsPage />} />
          <Route path="/team-chat/calls/:callId" element={<VoiceCallPage />} />
          {/* Firmio Meet, the colleagues' side. `history` is a static segment,
              so it can never be taken for a meeting code (12 random chars). */}
          <Route path="/meet" element={<MeetPage />} />
          <Route path="/meet/history/:id" element={<MeetHistoryPage />} />
          <Route path="/team-chat/:conversationId" element={<TeamChatPage />} />

          {/* CLE-265: Knowledge Base — markdown-first internal wiki. */}
          <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
          <Route path="/kb" element={<KnowledgeBasePage />} />

          <Route path="/settings" element={<SettingsPage />} />
          {/* CLE-193: lead pipeline & stage configuration (spec 3.4). */}
          <Route path="/settings/leads" element={<SettingsPage initialTab="leads" />} />
          {/* CLE-310: email delivery log + open tracking (ADMIN-only tab). */}
          <Route path="/settings/emails" element={<SettingsPage initialTab="email-history" />} />
          {/* Reported bugs are a page of their own, not a Settings tab: the
              tab strip there scrolls sideways and the list was off-screen.
              `/settings/bugs` stays as an alias so older links still land. */}
          <Route
            path="/bug-reports"
            element={
              <ProtectedRoute roles={['ADMIN']}>
                <BugReportsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/settings/bugs" element={<Navigate to="/bug-reports" replace />} />
          {/* Multi-company: firms list tab + per-firm detail page (incl. SmartBill). */}
          <Route path="/settings/companies" element={<SettingsPage initialTab="companies" />} />
          <Route path="/settings/companies/:id" element={<CompanyDetailPage />} />

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>

      {/* PWA install + update prompts (CLE-57) — non-modal, dismissible. */}
      <PwaPrompts />
    </>
  );
}
